/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { Injectable, inject } from "@angular/core";
import { environment } from "src/environments/environment";
import { ActionLoggerService } from "../generic/action-logger.service";

export enum LogLevel {
	None = 0,
	Error = 1,
	Warning = 2,
	Info = 3,
	Debug = 4,
}

/**
 * Service for logging messages with different log levels.
 */
@Injectable({
	providedIn: "root",
})
export class LoggerService {
	private readonly actionLoggerService: ActionLoggerService =
		inject(ActionLoggerService);

	public level: LogLevel = environment.production
		? LogLevel.Warning
		: LogLevel.Warning;

	/**
	 * Logs an informational message.
	 * @param args - The message or objects to be logged.
	 */
	logInfo(...args: any[]) {
		if (this.level >= LogLevel.Info) {
			console.info("INFO", ...args);
		}
	}

	/**
	 * Logs a warning message.
	 * @param args - The message or objects to be logged.
	 */
	logWarning(...args: any[]) {
		if (this.level >= LogLevel.Warning) {
			console.warn("WARNING", ...args);
			this.actionLoggerService.registerAction(
				`Warning logged: "${args.join(" ")}"`,
			);
		}
	}

	/**
	 * Logs an error message.
	 * @param args - The message or objects to be logged.
	 */
	logError(...args: any[]) {
		if (this.level >= LogLevel.Error) {
			console.error("ERROR", ...args);
			console.trace("TRACE");
			this.actionLoggerService.registerAction(
				`Error logged: "${args.join(" ")}"`,
			);
			//console.trace('TRACE');
		}
	}

	/**
	 * Logs a debug message.
	 * @param args - The message or objects to be logged.
	 */
	logDebug(...args: any[]) {
		if (this.level >= LogLevel.Debug) {
			console.debug("DEBUG", ...args);
		}
	}

	/**
	 * Sets the log level.
	 * @param level - The log level to be set.
	 */
	setLevel(level: LogLevel) {
		this.level = level;
	}
}
